'use client';

// SuggestedQuestions - Consumer chat suggested question chips
// Story 4.10: Suggested Questions for New Conversations
//
// Adapted from creator's SuggestedQuestions.tsx with consumer styling:
// - Simpler theme (no used-question tracking, chips disappear after first send)
// - Mobile-first with horizontal scroll and 48px touch targets
// - RTL support via CSS logical properties

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { useTranslations } from 'next-intl';

// Inline emoji helper — lightweight subset of creator's questionEmojis.ts
const emojiKeywords: Array<[string, string]> = [
  ['reschedule', '🔄'],
  ['directions', '🗺️'],
  ['schedule', '⏰'],
  ['location', '📍'],
  ['parking', '🚗'],
  ['address', '📍'],
  ['contact', '📞'],
  ['cancel', '❌'],
  ['refund', '💵'],
  ['hours', '⏰'],
  ['price', '💰'],
  ['where', '📍'],
  ['book', '📝'],
  ['cost', '💰'],
  ['free', '🆓'],
  ['open', '⏰'],
  ['time', '⏰'],
  ['what', '💡'],
  ['when', '⏰'],
  ['how', '💡'],
  ['who', '👤'],
  ['why', '💡'],
];

function getQuestionEmoji(question: string): string {
  const q = question.toLowerCase();
  for (const [keyword, emoji] of emojiKeywords) {
    if (q.includes(keyword)) return emoji;
  }
  return '💬';
}

export interface SuggestedQuestionsProps {
  questions: string[];
  isLoading?: boolean;
  onSelect: (question: string) => void;
  /** Questions that have already been used (shown with muted style) */
  usedQuestions?: Set<string>;
}

export function SuggestedQuestions({
  questions,
  isLoading = false,
  onSelect,
  usedQuestions,
}: SuggestedQuestionsProps) {
  const t = useTranslations('consumer.bot');

  if (isLoading) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('suggestedQuestions.title')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            pb: 0.5,
          }}
        >
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width={140 + i * 20}
              height={36}
              sx={{ borderRadius: 4 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (questions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: 1 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
      >
        {t('suggestedQuestions.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          pb: 0.5,
        }}
      >
        {questions.map((question) => {
          const emoji = getQuestionEmoji(question);
          const isUsed = usedQuestions?.has(question) ?? false;

          return (
            <Chip
              key={question}
              label={`${emoji} ${question}`}
              onClick={isUsed ? undefined : () => onSelect(question)}
              disabled={isUsed}
              sx={{
                backgroundColor: isUsed ? 'grey.100' : 'white',
                border: '1px solid',
                borderColor: isUsed ? 'grey.300' : 'primary.main',
                color: isUsed ? 'text.disabled' : 'primary.main',
                cursor: isUsed ? 'default' : 'pointer',
                height: 'auto',
                minHeight: { xs: 48, md: 36 },
                fontSize: { xs: '0.9rem', md: '0.8125rem' },
                opacity: isUsed ? 0.6 : 1,
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  px: { xs: 1.5, md: 1 },
                  py: 0.5,
                },
                '&:hover': isUsed
                  ? {}
                  : {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                // Override MUI disabled styles
                '&.Mui-disabled': {
                  opacity: 0.6,
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default SuggestedQuestions;
